import React, { useState, useEffect, useRef } from 'react';

interface Occupation {
  id: number;
  name: string;
  job_listing_total_count: number;
  kind?: string | null;
  children?: Occupation[];
}

type KindType = 'engineer' | 'marketer' | 'designer' | 'director' | 'consultant';

interface KindCategory {
  kind: KindType;
  label: string;
  order: number;
}

interface OccupationsFilterModalProps {
  show: boolean;
  onClose: () => void;
  onApply: (occupationIds: number[]) => void;
  selectedOccupationIds: number[];
}

const OccupationsFilterModal: React.FC<OccupationsFilterModalProps> = ({
  show,
  onClose,
  onApply,
  selectedOccupationIds
}) => {
  const [occupations, setOccupations] = useState<Occupation[]>([]);
  const [searchResults, setSearchResults] = useState<Occupation[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [tempSelected, setTempSelected] = useState<number[]>(selectedOccupationIds);
  const [expandedParentIds, setExpandedParentIds] = useState<(number | string)[]>([]);

  useEffect(() => {
    if (show) {
      setTempSelected(selectedOccupationIds);
      fetchOccupations();
      setSearchQuery('');
      setSearchResults([]);
      setExpandedParentIds([]);
    }
  }, [show, selectedOccupationIds]);

  // 検索キーワードが変更されたときに検索APIを呼び出す
  useEffect(() => {
    const controller = new AbortController();
    const term = searchQuery.trim();

    const run = async () => {
      if (term.length === 0) {
        // 検索クエリが空の場合は、検索結果をクリア（親職種を表示するため）
        setSearchResults([]);
        setSearchLoading(false);
        return;
      }

      setSearchLoading(true);
      try {
        const url = `/api/v1/occupations/search?keyword=${encodeURIComponent(term)}`;
        const response = await fetch(url, {
          headers: { Accept: 'application/json' },
          signal: controller.signal
        });
        if (!response.ok) {
          throw new Error(`Failed to search occupations (${response.status})`);
        }
        const data: Occupation[] = await response.json();
        setSearchResults(Array.isArray(data) ? data : []);
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          console.error('Occupation search failed', error);
          setSearchResults([]);
        }
      } finally {
        setSearchLoading(false);
      }
    };

    const id = window.setTimeout(run, 200);
    return () => {
      window.clearTimeout(id);
      controller.abort();
      setSearchLoading(false);
    };
  }, [searchQuery, occupations]);


  const fetchOccupations = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/occupations');
      const data = await response.json();
      setOccupations(data);
    } catch (error) {
      console.error('Failed to fetch occupations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOccupationToggle = (occupationId: number) => {
    const newSelection = tempSelected.includes(occupationId) 
      ? tempSelected.filter(id => id !== occupationId)
      : [...tempSelected, occupationId];
    
    setTempSelected(newSelection);
  };

  const handleApply = () => {
    onApply(tempSelected);
    onClose();
  };

  const handleClearAll = () => {
    setTempSelected([]);
  };

  // kindカテゴリの定義（番号順に並べる）
  const kindCategories: KindCategory[] = [
    { kind: 'engineer', label: 'エンジニア', order: 1 },
    { kind: 'marketer', label: 'マーケター', order: 2 },
    { kind: 'designer', label: 'デザイナー', order: 3 },
    { kind: 'director', label: 'ディレクター', order: 4 },
    { kind: 'consultant', label: 'コンサルタント', order: 5 }
  ];

  // すべての職種（親と子を含む）を平坦化
  const getAllOccupations = (): Occupation[] => {
    const all: Occupation[] = [];
    occupations.forEach(parent => {
      all.push(parent);
      if (parent.children) {
        all.push(...parent.children);
      }
    });
    return all;
  };

  // 親職種のみを取得（APIレスポンスは既に親職種のみを返しているため、すべてを返す）
  const getParentOccupations = (): Occupation[] => {
    return occupations;
  };

  // 親職種の展開/折りたたみを切り替え
  const handleParentToggle = (parentId: number | string) => {
    setExpandedParentIds(prev => 
      prev.includes(parentId) 
        ? prev.filter(id => id !== parentId)
        : [...prev, parentId]
    );
  };

  // kindに属する職種を取得
  const getOccupationsByKind = (kind: KindType): Occupation[] => {
    const allOccupations = getAllOccupations();
    return allOccupations.filter(occ => occ.kind === kind);
  };

  // kindに属する職種数を取得
  const getKindCount = (kind: KindType): number => {
    return getOccupationsByKind(kind).length;
  };

  if (!show) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="modal-backdrop fade show" 
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 10060 
        }}
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className="modal fade show d-block" 
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 10061,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        tabIndex={-1}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div className="modal-dialog modal-dialog-scrollable modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">職種を選択</h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={onClose}
                aria-label="閉じる"
              />
            </div>
            
            <div className="modal-body">
        
              {/* 選択/解除ボタン */}
              <div className="d-flex gap-2 mb-3">
                <button 
                  className="btn btn-sm btn-outline-danger"
                  onClick={handleClearAll}
                >
                  一括解除
                </button>
                <div className="ms-auto text-muted small">
                  {tempSelected.length}件選択中
                </div>
              </div>

              {/* 検索フィールド */}
              <div className="mb-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="職種名で検索..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                />
              </div>
              
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">読み込み中...</span>
                  </div>
                </div>
              ) : searchQuery.trim().length > 0 ? (
                /* 検索クエリがある場合: 検索結果を表示 */
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {searchLoading ? (
                    <div className="text-center py-3">検索中...</div>
                  ) : searchResults.length > 0 ? (
                    <div className="row g-2">
                      {searchResults.map(occupation => (
                        <div key={occupation.id} className="col-6 col-md-4 col-lg-3">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`occupation-search-${occupation.id}`}
                              checked={tempSelected.includes(occupation.id)}
                              onChange={() => handleOccupationToggle(occupation.id)}
                            />
                            <label className="form-check-label" htmlFor={`occupation-search-${occupation.id}`} style={{ cursor: 'pointer' }}>
                              {occupation.name} ({occupation.job_listing_total_count || 0})
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-muted py-3">
                      検索結果が見つかりませんでした
                    </div>
                  )}
                </div>
              ) : (
                /* 検索クエリが空の場合: 親職種をツリー構造で表示 */
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {getParentOccupations().length > 0 ? (
                    <div className="accordion">
                      {getParentOccupations().map(parent => {
                        // 「その他」カテゴリの場合は特別なIDを使用
                        const parentKey = parent.id ?? 'other';
                        const isExpanded = expandedParentIds.includes(parentKey);
                        const childrenCount = parent.children?.length || 0;
                        
                        return (
                          <div key={parentKey} className="accordion-item">
                            <h2 className="accordion-header">
                              <button 
                                className={`accordion-button ${isExpanded ? '' : 'collapsed'}`}
                                type="button" 
                                onClick={() => handleParentToggle(parentKey)}
                                style={{ cursor: 'pointer' }}
                              >
                                <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} me-2`}></i>
                                {parent.name} ({childrenCount})
                              </button>
                            </h2>
                            {isExpanded && (
                              <div className="accordion-collapse collapse show">
                                <div className="accordion-body">
                                  <div className="row g-2">
                                    {parent.children?.map(child => (
                                      <div key={child.id} className="col-6 col-md-4 col-lg-3">
                                        <div className="form-check">
                                          <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`occupation-child-${child.id}`}
                                            checked={tempSelected.includes(child.id)}
                                            onChange={() => handleOccupationToggle(child.id)}
                                          />
                                          <label className="form-check-label" htmlFor={`occupation-child-${child.id}`} style={{ cursor: 'pointer' }}>
                                            {child.name} ({child.job_listing_total_count || 0})
                                          </label>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center text-muted py-3">
                      職種を読み込み中...
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={onClose}
              >
                キャンセル
              </button>
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={handleApply}
              >
                この条件で検索
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OccupationsFilterModal;